/**
 * Checklists Store (Pinia) — E6 checklist AFFIDATE
 *
 * @version 1.0.0
 * @description Lato AFFIDATARIO: le checklist di sopralluogo che un tecnico
 *              ha affidato all'utente corrente (`/api/checklists/*`, fuori da
 *              /commesse: chi compila non vede le commesse). Bozza, foto,
 *              "Chiudi checklist", e il conteggio delle aperte che accende la
 *              voce di menu CHECKLIST.
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'
import { standardizeErrorMessage } from '@/utils/utils'
import type { ChecklistAssignment, ChecklistAssignmentDetail } from '@/models/commesse'

export const useChecklistsStore = defineStore('checklists', () => {
  const baseUrl = import.meta.env.VITE_API_URL as string

  const loading = ref(false)
  const items = ref<ChecklistAssignment[]>([])
  const current = ref<ChecklistAssignmentDetail | null>(null)
  /** Checklist aperte affidate a me: > 0 accende la voce di menu */
  const openCount = ref(0)

  const url = (suffix = '') => `${baseUrl}/checklists${suffix}`

  async function fetchOpenCount(): Promise<number> {
    try {
      const { data } = await axios.get(url('/open-count'))
      openCount.value = Number(data?.open ?? 0)
    } catch {
      openCount.value = 0
    }
    return openCount.value
  }

  async function fetchMine(): Promise<ChecklistAssignment[]> {
    loading.value = true
    try {
      const { data } = await axios.get(url())
      items.value = data.data ?? []
      openCount.value = items.value.filter((a) => a.status === 'open').length
      return items.value
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  async function fetchOne(token: string): Promise<ChecklistAssignmentDetail> {
    loading.value = true
    try {
      const { data } = await axios.get(url(`/${token}`))
      current.value = data
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /** Autosave della bozza: il BE tiene solo le chiavi previste dalla checklist. */
  async function saveDraft(token: string, data: Record<string, any>): Promise<ChecklistAssignmentDetail> {
    try {
      const res = await axios.put(url(`/${token}`), { data })
      current.value = res.data
      return res.data
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  async function uploadPhoto(token: string, file: File): Promise<ChecklistAssignmentDetail> {
    const formData = new FormData()
    formData.append('file', file, file.name)
    try {
      const res = await axios.post(url(`/${token}/photos`), formData, {
        headers: { 'content-type': 'multipart/form-data' },
      })
      current.value = res.data
      return res.data
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  async function deletePhoto(token: string, attachmentId: number): Promise<ChecklistAssignmentDetail> {
    try {
      const res = await axios.delete(url(`/${token}/photos/${attachmentId}`))
      current.value = res.data
      return res.data
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  /** Blob autenticato → objectURL (cache per id), come le anteprime della commessa. */
  const previewUrls = new Map<number, string>()
  async function fetchPhotoPreview(token: string, attachmentId: number): Promise<string> {
    const cached = previewUrls.get(attachmentId)
    if (cached) return cached
    const response = await axios.get(url(`/${token}/photos/${attachmentId}`), { responseType: 'blob' })
    const blobUrl = window.URL.createObjectURL(response.data)
    previewUrls.set(attachmentId, blobUrl)
    return blobUrl
  }

  async function submit(token: string): Promise<ChecklistAssignmentDetail> {
    try {
      const res = await axios.post(url(`/${token}/submit`))
      current.value = res.data
      await fetchOpenCount()
      return res.data
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  function reset(): void {
    items.value = []
    current.value = null
    openCount.value = 0
  }

  return {
    loading,
    items,
    current,
    openCount,
    fetchOpenCount,
    fetchMine,
    fetchOne,
    saveDraft,
    uploadPhoto,
    deletePhoto,
    fetchPhotoPreview,
    submit,
    reset,
  }
})
